import React, { useEffect, useState, useMemo } from 'react';

import Paper from '@mui/material/Paper';
import PropTypes from 'prop-types';

import useMediaQuery from '@mui/material/useMediaQuery';
import DeviceRotate from 'src/component/UI/DeviceRotate';
import variables from 'src/style/variable/variables.module.scss';
import Button from 'src/component/UI/Button';
import DataTable from 'src/component/UI/DataTable';
import { formatDate } from 'src/utils/utils';
import { List } from 'immutable';
import { getTablePagePreferences } from 'src/module/authentication/selector';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDeviceCheckSheets } from 'src/module/device/action';
import { getAlarmCheckSheetReports, getDeviceCheckSheetLoading, getDeviceCheckSheetTotal, getDeviceCheckSheetTotalPages, getDeviceById } from 'src/module/device/selector';
import { closeCheckSheet, fetchCheckSheetTasks, openCheckSheet, downloadCommissioningPdf } from 'src/module/checkSheet/action';
import { getCheckSheetReportIsOpen } from 'src/module/checkSheet/selector';
import PermissionFence from 'src/component/PermissionFence';
import CommissioningStatusIcon from './CommissioningStatusIcon';
import { useTranslation } from 'src/i18n';
import { usePDFServices } from 'src/hooks/useReportData';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadSharp';

interface GenericCommissioningContentProps {
  deviceId: string;
  checkSheetType: string;
  titleKey: string;
  createReportButtonKey: string;
  tableId: string;
  contentTestId: string;
  fetchTaskType: string;
  ReportComponent: React.ComponentType<any>;
}

function formatName (data) {
  return data.get('first_name') + ' ' + data.get('last_name');
}

export default function GenericCommissioningContent (props: GenericCommissioningContentProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    deviceId,
    checkSheetType,
    titleKey,
    createReportButtonKey,
    tableId,
    contentTestId,
    fetchTaskType,
    ReportComponent
  } = props;

  const { usePDFServicesValue: pdfServicesEnabled } = usePDFServices();
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    dispatch(closeCheckSheet(checkSheetType));
  }, []);
  const [selected, setSelected] = useState(null);
  const showModal = useSelector(getCheckSheetReportIsOpen(checkSheetType));
  const totalPages = useSelector(getDeviceCheckSheetTotalPages(deviceId, checkSheetType));
  const total = useSelector(getDeviceCheckSheetTotal(deviceId, checkSheetType));
  const loading = useSelector(getDeviceCheckSheetLoading(deviceId, checkSheetType));

  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const [showRotateMessage, setShowRotateMessage] = useState(isMobile && isPortrait);
  useEffect(() => {
    setShowRotateMessage(isMobile && isPortrait);
  }, [isMobile, isPortrait]);
  useEffect(() => {
    if (collapsed) {
      dispatch(closeCheckSheet(checkSheetType));
      setSelected(null);
    }
  }, [collapsed]);

  useEffect(() => {
    if (deviceId && !collapsed && !showRotateMessage) {
      dispatch(fetchCheckSheetTasks(fetchTaskType));
    }
  }, [deviceId, collapsed, showRotateMessage, fetchTaskType, dispatch]);

  const userPagePreference = useSelector(getTablePagePreferences(tableId));
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(userPagePreference);
  const [sort, setSort] = useState(null);
  const fetchPage = (_page, _pageSize, _filter, _sort) => {
    setPage(_page);
    setPageSize(_pageSize);
    setSort(_sort);
  };
  useEffect(() => {
    if (deviceId && !collapsed && !showRotateMessage) {
      const paginationDetails = { page, pageSize, sort };
      dispatch(fetchDeviceCheckSheets(checkSheetType, deviceId, paginationDetails));
    }
  }, [deviceId, page, pageSize, sort, collapsed, showRotateMessage, checkSheetType, dispatch]);

  const reports = useSelector(getAlarmCheckSheetReports(deviceId, checkSheetType));
  const onRowClick = (row) => {
    if (!row.original.get('file_id')) {
      setSelected(row.original.get('id'));
      dispatch(openCheckSheet(checkSheetType));
    }
  };
  const onClickCreate = () => {
    dispatch(openCheckSheet(checkSheetType));
    setSelected(null);
  };
  const handleClose = () => {
    dispatch(closeCheckSheet(checkSheetType));
  };

  const deviceInfo = useSelector(getDeviceById(deviceId));
  const circuitTag = deviceInfo?.get('tag') ?? null;
  const parentTag = deviceInfo?.get('parent_tag') ?? null;
  const deviceTag = checkSheetType === 'commissioning-circuit' && parentTag
    ? `${parentTag}-${circuitTag}`
    : circuitTag;

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        id: 'notes',
        Header: t('alarms.notes'),
        accessor: (row) => row.get('notes'),
        Cell: (row: any) => row.cell.value,
      },
      {
        id: 'created_by',
        Header: t('testingReports.createdBy'),
        accessor: (row) => row.get('created_by'),
        Cell: (row: any) => formatName(row.cell.row.original),
      },
      {
        id: 'created_at',
        Header: t('testingReports.createdAt'),
        accessor: (row) => row.get('created_at'),
        Cell: (row: any) => (row.cell.value ? (<>{formatDate(row.cell.value)}</>) : ''),
      },
      {
        id: 'commissioning_status',
        Header: t('common.commissioningStatus'),
        accessor: (row) => row.get('commissioning_status'),
        Cell: (row: any) => <CommissioningStatusIcon status={row.cell.value} />,
      },
    ];
    if (pdfServicesEnabled) {
      const iconStyles = { color: variables.primarySolid };

      cols.push({
        id: 'pdf_export',
        Header: '',
        accessor: (row) => row.get('id'),
        Cell: (row: any) => (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              dispatch(downloadCommissioningPdf(row.cell.value, checkSheetType, deviceTag));
            }}
          >
            <FileDownloadOutlinedIcon style={iconStyles}/>
          </Button>
        ),
      });
    }
    return cols;
  }, [t, pdfServicesEnabled, checkSheetType, deviceTag, dispatch]);

  return (
    <div id="testing-reports-content" className='testing-reports-content' data-testid={contentTestId}>
      <Paper>
        <h3 className='expandable' onClick={() => setCollapsed(!collapsed)}>
          {t(titleKey)} &nbsp;
          <span className='expand-arrow'>{collapsed ? '▼' : '▲'}</span>
        </h3>

        {!collapsed ?
          <div className="content-wrapper">
            <div className='create-button'>
              <PermissionFence can='edit-device'>
                <Button className='create-report-button' cta onClick={onClickCreate}>{t(createReportButtonKey)}</Button>
              </PermissionFence>
            </div>
            <ReportComponent open={showModal} reportId={selected} deviceId={deviceId} handleClose={handleClose} />
            {showRotateMessage ?
              <DeviceRotate />
              : <DataTable
                filterable={false}
                filterLabel='Search Reports'
                pagination={true}
                numPages={totalPages}
                totalRecords={total}
                loading={loading}
                data={List(reports)}
                columns={columns}
                fetchPage={fetchPage}
                handleCellClick={onRowClick}
                columnWidths={pdfServicesEnabled ? ['30%', '25%', '20%', '20%', '5%'] : ['30%', '25%', '20%', '25%']}
                placeholder={<><span>{t('testingReports.noReports')} </span><span role='img' aria-label='Sweat Smile'>😅</span></>}
                tableId={tableId}
              />}
          </div>
          : null}
      </Paper>
    </div>
  );
}

GenericCommissioningContent.propTypes = {
  deviceId: PropTypes.string,
  checkSheetType: PropTypes.string.isRequired,
  titleKey: PropTypes.string.isRequired,
  createReportButtonKey: PropTypes.string.isRequired,
  tableId: PropTypes.string.isRequired,
  contentTestId: PropTypes.string.isRequired,
  fetchTaskType: PropTypes.string.isRequired,
  ReportComponent: PropTypes.elementType.isRequired,
};

GenericCommissioningContent.defaultProps = {
  deviceId: null,
};
